import { t } from 'i18next';
import { useEffect, useState } from 'react';
import Modal from '../../../../../components/Modal';
import type { Order } from '../../../../../types/apiResponse/order';
import OrderLocationMap from './OrderLocationMap';
import {
  getOrderCoordinates,
  getOpenStreetMapUrl,
  getReverseGeocodeAddress,
  type ReverseGeocodeAddress,
} from './location';
import * as s from './styles';

type Props = {
  order: Order;
  showModal: boolean;
  closeModal: () => void;
};

type AddressStatus = 'loading' | 'available' | 'unavailable';

const formatStreet = (address: ReverseGeocodeAddress) => (
  [address.houseNumber, address.road].filter(Boolean).join(', ')
);

const formatLocality = (address: ReverseGeocodeAddress) => (
  [address.neighbourhood || address.suburb, [address.city, address.state].filter(Boolean).join(' - ')]
    .filter(Boolean)
    .join(' • ')
);

const OrderLocationModal = ({ order, showModal, closeModal }: Props) => {
  const coordinates = getOrderCoordinates(order);
  const latitude = coordinates?.latitude ?? null;
  const longitude = coordinates?.longitude ?? null;
  const [address, setAddress] = useState<ReverseGeocodeAddress | null>(null);
  const [addressStatus, setAddressStatus] = useState<AddressStatus>('loading');

  useEffect(() => {
    if (latitude === null || longitude === null) {
      setAddressStatus('unavailable');
      return undefined;
    }

    const lookupCoordinates = { latitude, longitude };
    let isDisposed = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);
    setAddress(null);
    setAddressStatus('loading');

    void getReverseGeocodeAddress(lookupCoordinates, controller.signal)
      .then((result) => {
        if (isDisposed) return;
        setAddress(result);
        setAddressStatus(result ? 'available' : 'unavailable');
      })
      .catch(() => {
        if (!isDisposed) setAddressStatus('unavailable');
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      isDisposed = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [latitude, longitude]);

  if (!coordinates) return null;

  const street = address ? formatStreet(address) : '';
  const locality = address ? formatLocality(address) : '';
  const addressTitle = address?.name || street || address?.displayName || (t('orderLocationApproximate') as string);
  const mapUrl = getOpenStreetMapUrl(coordinates);

  return (
    <Modal
      title={t('orderLocationTitle', { orderNumber: order.NumeroPedido }) as string}
      showModal={showModal}
      closeModal={closeModal}
      onlyCloseButton
      style={{ width: 'min(920px, 94vw)', maxWidth: '94vw' }}
      bodyStyle={{ padding: '4.5rem 1rem 1rem' }}
    >
      <s.Content>
        <s.LocationDetails>
          <s.AddressTitle>{addressTitle as string}</s.AddressTitle>
          {street && street !== addressTitle && <s.AddressLine>{street}</s.AddressLine>}
          {locality && <s.AddressLine>{locality}</s.AddressLine>}
          {address?.postcode && (
            <s.LocationMeta>
              <span>{t('postalCode') as string}: {address.postcode}</span>
            </s.LocationMeta>
          )}
          {addressStatus === 'loading' && (
            <s.AddressLine role="status" aria-live="polite">{t('orderLocationAddressLoading') as string}</s.AddressLine>
          )}
          {addressStatus === 'unavailable' && (
            <s.AddressLine role="status">{t('orderLocationAddressUnavailable') as string}</s.AddressLine>
          )}
          <s.LocationMeta>
            <span>
              {t('coordinates') as string}: {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
            </span>
            <a href={mapUrl} target="_blank" rel="noreferrer">{t('openInOpenStreetMap') as string}</a>
          </s.LocationMeta>
        </s.LocationDetails>
        <OrderLocationMap coordinates={coordinates} />
      </s.Content>
    </Modal>
  );
};

export default OrderLocationModal;
